/**
 * Web Push through a service worker and a VAPID key. The worker script must
 * import installPushHandlers from `@mp/adapters/push/sw`.
 */
import type { PushPermission, PushProvider, PushSubscriptionInfo } from './types.js';
import { arrayBufferToBase64Url, urlBase64ToUint8Array } from './vapid.js';

export interface WebPushOptions {
  /** base64url P-256 public key; the private half stays on the API */
  vapidPublicKey: string;
  /** service worker URL (registered on subscribe if not yet registered) */
  swUrl?: string;
  navigator?: Navigator;
  notification?: typeof Notification;
}

const mapPermission = (p: NotificationPermission): PushPermission => (p === 'default' ? 'prompt' : p);

export class WebPushProvider implements PushProvider {
  readonly kind = 'web';

  constructor(private readonly o: WebPushOptions) {}

  private get nav(): Navigator {
    return this.o.navigator ?? navigator;
  }

  private get notif(): typeof Notification | undefined {
    return this.o.notification ?? (globalThis as { Notification?: typeof Notification }).Notification;
  }

  private get supported(): boolean {
    return 'serviceWorker' in this.nav && this.notif !== undefined && 'PushManager' in globalThis;
  }

  async permissionState(): Promise<PushPermission> {
    return this.supported ? mapPermission(this.notif!.permission) : 'unsupported';
  }

  async requestPermission(): Promise<PushPermission> {
    return this.supported ? mapPermission(await this.notif!.requestPermission()) : 'unsupported';
  }

  async subscribe(): Promise<PushSubscriptionInfo | null> {
    if ((await this.permissionState()) !== 'granted') return null;
    const reg = await this.registration();
    const sub =
      (await reg.pushManager.getSubscription()) ??
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(this.o.vapidPublicKey) }));
    return {
      kind: 'webpush',
      endpoint: sub.endpoint,
      keys: { p256dh: arrayBufferToBase64Url(sub.getKey('p256dh')), auth: arrayBufferToBase64Url(sub.getKey('auth')) },
    };
  }

  async unsubscribe(): Promise<void> {
    if (!this.supported) return;
    const reg = await this.nav.serviceWorker.getRegistration(this.o.swUrl);
    await (await reg?.pushManager.getSubscription())?.unsubscribe();
  }

  private async registration(): Promise<ServiceWorkerRegistration> {
    const sw = this.nav.serviceWorker;
    return (await sw.getRegistration(this.o.swUrl)) ?? (await sw.register(this.o.swUrl ?? '/sw.js', { type: 'module' }));
  }
}
